import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  TooltipProps
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';
import round from 'helpers/mathRound';
import {
  findMinValue,
  numberFormat
} from '../utils/sales';
import '../styles/layout.css';


const CustomizedAxisTick = (props) => {
  const { x, y, payload, data } = props;

  return (
    <g transform={`translate(${x},${y}) rotate(-45)`}>
      <text x={10} y={0} textAnchor="end" fill="#666" fontSize={12}>
        {payload.value.substring(0, 3)} {data[payload.index].year}
      </text>
    </g>
  );
};

const CustomTooltip = (props: TooltipProps<ValueType, NameType>) => {
  const { active, payload, label } = props;

  if (active && payload) {
    const difference = ((Number(payload[0].value) - Number(payload[1].value)) / Number(payload[1].value)) * 100;
    return (
      <div className="bg-white p-2">
        <p className="font-bold">{`${label} ${payload[0].payload.year}`}</p>
        {payload.map((item) => {
          const { name, value, color } = item;
          return (
            <div key={name} className="text-gray flex items-center">
              <div style={{background: color, width: '10px', height: '10px', borderRadius: '100%', marginRight: '5px'}} />
              <div>
                {`${name}: ${
                  numberFormat(
                    +value,
                    'currency',
                    'long',
                    0,
                    'standard'
                  )}`
                }
              </div>
            </div>
          );
        })}
        <p className="text-gray">{`% Difference ${round(difference, 0)}%`}</p>
      </div>
    );
  }

  return null;
};

const RevenueAnnualComparison = ({data, width}) => {
  const [opacity, setOpacity] = React.useState({current_year_revenue: 1, last_year_revenue: 1});

  const lowestRevenueValue = findMinValue(
    data.annual_sales_per_month,
    'current_year_revenue',
    'last_year_revenue'
  );

  const handleMouseEnter = (o) => {
    const { dataKey } = o;
    setOpacity({ ...opacity, [dataKey]: 0.4 });
  };

  const handleMouseLeave = (o) => {
    const { dataKey } = o;
    setOpacity({ ...opacity, [dataKey]: 1 });
  };

  return (
    <div className="grid-item">
      <h4 className="px-5 py-2 mb-4 text-light-gray text-center bg-light-blue">
        Revenue Annual Comparison
      </h4>
      <div>
        <LineChart
          data={data.annual_sales_per_month}
          width={width}
          height={width * 0.6}
          margin={{
            top: 5,
            right: 50,
            left: 20,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            tickMargin={25}
            padding={{ left: 10, right: 10}}
            interval={0}
            tick={<CustomizedAxisTick data={data.annual_sales_per_month} />}
          />
          <YAxis domain={[lowestRevenueValue, 'auto']} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            height={36}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
          <Line
            name="Revenue this year"
            dataKey="current_year_revenue"
            stroke="#8884d8"
            activeDot={{ r: 8 }}
            strokeOpacity={opacity.last_year_revenue}
            strokeWidth={2}
            type="linear"
          />
          <Line
            name="Revenue last year"
            type="linear"
            dataKey="last_year_revenue"
            stroke="#82ca9d"
            strokeOpacity={opacity.current_year_revenue}
            strokeWidth={2}
          />
        </LineChart>
      </div>
    </div>
  );
};

export default RevenueAnnualComparison;
